import { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { SignedIn } from '@clerk/clerk-react';
import { api } from '../utils/api';
import { useCart } from '../contexts/CartContext';
import ImageWithPlaceholder from '../components/ImageWithPlaceholder';
import BackToTop from '../components/BackToTop';

const SLIDE_MS = 320;

export default function VendorPublic() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [vendor, setVendor] = useState(null);
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [reviewsExpanded, setReviewsExpanded] = useState(false);
  const [itemSearchQuery, setItemSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('all'); // 'all' | categoryId
  const [slideDir, setSlideDir] = useState(0); // -1 left, 1 right
  const [animating, setAnimating] = useState(false);
  const tabBarRef = useRef(null);
  const tabRefs = useRef({});
  const [indicator, setIndicator] = useState({ left: 0, width: 0 });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [vendorRes, itemsRes, catsRes] = await Promise.all([
          api(`/api/vendors/${id}`, {}),
          api(`/api/items/vendor/${id}`, {}),
          api(`/api/categories/vendor/${id}`, {}),
        ]);
        if (!vendorRes.ok) {
          const data = await vendorRes.json().catch(() => ({}));
          throw new Error(data.error || `Error ${vendorRes.status}`);
        }
        if (!itemsRes.ok) {
          const data = await itemsRes.json().catch(() => ({}));
          throw new Error(data.error || `Error ${itemsRes.status}`);
        }
        const vendorData = await vendorRes.json();
        const itemsData = await itemsRes.json();
        const catsData = catsRes.ok ? await catsRes.json() : [];
        if (!cancelled) {
          setVendor(vendorData);
          setItems(itemsData);
          setCategories(catsData);
          setActiveTab('all');
        }
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  const tabs = useMemo(
    () => [{ id: 'all', name: 'All' }, ...categories.map((c) => ({ id: String(c.id), name: c.name }))],
    [categories]
  );

  const updateIndicator = (tabId) => {
    const el = tabRefs.current[tabId];
    const bar = tabBarRef.current;
    if (!el || !bar) return;
    const barRect = bar.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    setIndicator({
      left: elRect.left - barRect.left + bar.scrollLeft,
      width: elRect.width,
    });
  };

  useEffect(() => {
    updateIndicator(activeTab);
    const onResize = () => updateIndicator(activeTab);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [activeTab, tabs]);

  const selectTab = (nextId) => {
    if (nextId === activeTab || animating) return;
    const from = tabs.findIndex((t) => t.id === activeTab);
    const to = tabs.findIndex((t) => t.id === nextId);
    setSlideDir(to > from ? 1 : -1);
    setAnimating(true);
    setActiveTab(nextId);
    window.setTimeout(() => setAnimating(false), SLIDE_MS);
  };

  const renderStars = (stars) =>
    Array.from({ length: 5 }, (_, i) => (
      <span key={i} className={i < stars ? 'text-amber-400' : 'text-slate-300 dark:text-slate-600'}>
        ★
      </span>
    ));

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <p className="text-2xl text-slate-500 dark:text-slate-400">Loading vendor page…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col gap-4">
        <h1 className="text-3xl font-semibold">Vendor Store</h1>
        <p className="rounded-xl bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 p-4 text-lg">
          {error}
        </p>
      </div>
    );
  }

  if (!vendor) {
    return (
      <div className="flex flex-col gap-4">
        <h1 className="text-3xl font-semibold">Vendor Store</h1>
        <p className="text-lg text-slate-600 dark:text-slate-400">Vendor not found.</p>
      </div>
    );
  }

  const q = (itemSearchQuery || '').trim().toLowerCase();
  let filteredItems = items;
  if (activeTab !== 'all') {
    const catId = parseInt(activeTab, 10);
    filteredItems = filteredItems.filter((item) => item.categoryId === catId);
  }
  if (q) {
    filteredItems = filteredItems.filter((item) => {
      const title = (item.title || '').toLowerCase();
      const desc = (item.description || '').toLowerCase();
      return title.includes(q) || desc.includes(q);
    });
  }

  const slideClass =
    slideDir === 1
      ? 'vendor-items-slide-from-right'
      : slideDir === -1
        ? 'vendor-items-slide-from-left'
        : '';

  return (
    <div className="flex flex-col gap-6 max-w-screen-md mx-auto w-full">
      {vendor.marketId != null && (
        <button
          type="button"
          onClick={() => navigate(`/market/${vendor.marketId}`)}
          className="self-start inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-700 dark:text-emerald-400 hover:underline"
        >
          <span aria-hidden>←</span>
          {vendor.marketName ? `All vendors at ${vendor.marketName}` : 'Back to vendors'}
        </button>
      )}
      <div className="rounded-2xl overflow-hidden border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
        <div className="aspect-[21/9] bg-slate-200 dark:bg-slate-700">
          {vendor.storeImageUrl ? (
            <ImageWithPlaceholder
              src={vendor.storeImageUrl}
              alt={vendor.storeName || 'Store'}
              className="w-full h-full object-cover"
              containerClassName="w-full h-full"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-slate-400">
              Store photo
            </div>
          )}
        </div>
        <div className="p-5 flex flex-col items-center text-center gap-3">
          <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-50">
            {vendor.storeName || 'Unnamed Store'}
          </h1>
          {vendor.marketName && (
            <p className="text-sm text-slate-600 dark:text-slate-400">
              At <span className="font-medium text-slate-800 dark:text-slate-200">{vendor.marketName}</span>
              {vendor.locationText ? ` · Booth ${vendor.locationText}` : ''}
            </p>
          )}
          <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm text-slate-600 dark:text-slate-400">
            {vendor.storeHours && <span>Hours: {vendor.storeHours}</span>}
            {vendor.phone && <span>Phone: {vendor.phone}</span>}
          </div>
          {vendor.reviewCount > 0 && (
            <div className="flex items-center justify-center gap-2">
              <div className="flex">{renderStars(Math.round(vendor.averageStars || 0))}</div>
              <span className="text-sm text-slate-500">
                {(vendor.averageStars || 0).toFixed(1)} · {vendor.reviewCount} reviews
              </span>
            </div>
          )}
          <SignedIn>
            <button
              type="button"
              onClick={() => navigate(`/review/${id}?type=vendor`)}
              className="text-sm font-medium text-[#E47911] hover:underline"
            >
              Write a vendor review
            </button>
          </SignedIn>
        </div>
      </div>

      {vendor.reviewCount > 0 && (
        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={() => setReviewsExpanded(!reviewsExpanded)}
            className="text-center text-sm font-medium text-slate-700 dark:text-slate-300 hover:underline"
          >
            {reviewsExpanded ? 'Hide reviews' : `Show reviews (${vendor.reviewCount})`}
          </button>
          {reviewsExpanded && (
            <div className="flex flex-col gap-3">
              {(vendor.reviews || []).map((review) => (
                <div
                  key={review.id}
                  className="p-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-center"
                >
                  <div className="flex items-center justify-center gap-2 mb-1">
                    <div className="flex">{renderStars(review.stars)}</div>
                    <span className="text-xs text-slate-500">
                      {new Date(review.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                  {review.comment && (
                    <p className="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-line">
                      {review.comment}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="relative w-full">
        <input
          type="text"
          value={itemSearchQuery}
          onChange={(e) => setItemSearchQuery(e.target.value)}
          placeholder="Search this vendor’s items"
          className="w-full h-12 text-base px-4 pr-14 rounded-full border-0 bg-gray-100 dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-orange-400"
          aria-label="Search this vendor’s items"
        />
      </div>

      {/* Category nav — All always first & selected on load; empty categories hidden by API */}
      <div className="relative">
        <div
          ref={tabBarRef}
          className="relative flex gap-1 overflow-x-auto pb-2 -mx-1 px-1 scrollbar-thin"
          role="tablist"
          aria-label="Item categories"
        >
          {tabs.map((tab) => (
            <button
              key={tab.id}
              ref={(el) => { tabRefs.current[tab.id] = el; }}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.id}
              onClick={() => selectTab(tab.id)}
              className={`shrink-0 px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                activeTab === tab.id
                  ? 'text-emerald-800 dark:text-emerald-300'
                  : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100'
              }`}
            >
              {tab.name}
            </button>
          ))}
          <div
            className="absolute bottom-0 h-1 rounded-full bg-emerald-600 shadow-[0_0_8px_rgba(5,150,105,0.45)] pointer-events-none"
            style={{
              left: indicator.left,
              width: indicator.width,
              transition: `left ${SLIDE_MS}ms ease-out, width ${SLIDE_MS}ms ease-out`,
            }}
          />
        </div>
      </div>

      <h2 className="text-2xl font-semibold text-center">Items</h2>

      <div
        key={`${activeTab}-${q}`}
        className={slideClass}
        style={{ animationDuration: `${SLIDE_MS}ms` }}
      >
        {filteredItems.length === 0 ? (
          <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-8 text-center text-slate-600">
            {items.length === 0 ? 'No items available at this time.' : 'No items match.'}
          </div>
        ) : (
          <div
            className={
              filteredItems.length === 1
                ? 'w-full max-w-lg mx-auto'
                : 'grid grid-cols-2 sm:grid-cols-3 gap-3'
            }
          >
            {filteredItems.map((item) => {
              const imageUrls = item.imageUrls || [];
              const firstImage = imageUrls[0] || null;
              const isOutOfStock = item.stockCount <= 0;
              return (
                <div
                  key={item.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate(`/item/${item.id}`)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') navigate(`/item/${item.id}`);
                  }}
                  className="text-center rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-hidden hover:border-emerald-500 transition-colors cursor-pointer"
                >
                  <div className="aspect-square bg-slate-100 dark:bg-slate-700">
                    {firstImage ? (
                      <ImageWithPlaceholder
                        src={firstImage}
                        alt={item.title}
                        className="w-full h-full object-cover"
                        containerClassName="w-full h-full"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-xs text-slate-400">
                        No photo
                      </div>
                    )}
                  </div>
                  <div className="p-3 flex flex-col items-center gap-1">
                    <h3 className="font-semibold text-slate-900 dark:text-slate-50 line-clamp-2 text-sm leading-snug">
                      {item.title}
                    </h3>
                    <p className="text-base font-bold text-[#E47911]">
                      ${parseFloat(item.price || 0).toFixed(2)}
                    </p>
                    <p className="text-xs text-slate-500">
                      {isOutOfStock ? 'Out of stock' : `${item.stockCount} in stock`}
                    </p>
                    <SignedIn>
                      <button
                        type="button"
                        disabled={isOutOfStock}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!isOutOfStock) addItem(item.id, 1);
                        }}
                        className={`mt-1 px-3 py-1.5 rounded-lg text-sm font-medium ${
                          isOutOfStock
                            ? 'bg-slate-200 text-slate-500 cursor-not-allowed'
                            : 'bg-[#E47911] text-white hover:bg-[#C96A0C]'
                        }`}
                      >
                        Add to cart
                      </button>
                    </SignedIn>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <BackToTop />
    </div>
  );
}
